import React from 'react'

import {
	IClientInvoice,
	IClientInvoiceInput,
	createClientInvoice,
	deleteClientInvoice,
	listClientInvoices,
	markClientInvoicePaid,
	markClientInvoiceUnpaid,
} from '../../../Services/clientInvoices'

/**
 * Chargement et actions sur les factures d'un compte.
 *
 * Chaque action recharge la liste depuis l'API plutôt que de modifier l'état local :
 * marquer une facture payée déclenche une émission d'event côté serveur, et on préfère
 * afficher ce que la base dit vraiment plutôt qu'un optimisme qui masquerait un refus.
 */
export const useClientInvoices = (billingAccountId: string) => {
	const [invoices, setInvoices] = React.useState<IClientInvoice[] | null>(null)
	const [error, setError] = React.useState<string | null>(null)
	const [busy, setBusy] = React.useState(false)

	const load = React.useCallback(() => {
		setError(null)
		listClientInvoices(billingAccountId)
			.then((r) => setInvoices(r || []))
			.catch((e: Error) => setError(e?.message || 'Lecture des factures impossible'))
	}, [billingAccountId])

	React.useEffect(load, [load])

	// Les actions laissent remonter l'erreur : l'appelant décide quoi en dire. Elles
	// rechargent en cas de succès seulement.
	const run = async <T,>(action: () => Promise<T>) => {
		setBusy(true)
		try {
			const res = await action()
			load()
			return res
		} finally {
			setBusy(false)
		}
	}

	return {
		invoices,
		error,
		busy,
		reload: load,
		create: (data: IClientInvoiceInput) => run(() => createClientInvoice(billingAccountId, data)),
		markPaid: (id: number, paidAt?: string) => run(() => markClientInvoicePaid(billingAccountId, id, paidAt)),
		markUnpaid: (id: number) => run(() => markClientInvoiceUnpaid(billingAccountId, id)),
		remove: (id: number) => run(() => deleteClientInvoice(billingAccountId, id)),
	}
}
